import { useMutation, useQuery } from '@tanstack/react-query';
import { CLIENT_TIMEZONES, type ClientCreatedView, type ClientPreviewView, type PackageSummaryView, type TemplateSummaryView } from '@lp/contracts';
import { ArrowLeft, Check, Copy, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorText, fieldProblems, get, post } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Badge, Button, Card, ErrorBox, Field, Input, Loading, PageHeader, Select, cn } from '@/components/ui';

const STEPS = ['Company', 'Package and template', 'Admin', 'Check and create'] as const;

/** a suggestion for the address name from the English name: "Gold Lottery" → gold-lottery */
export const slugOf = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

/**
 * Create Company → Package → Starter Template → Admin → Address (master prompt §7). The server checks everything again on create and makes
 * the whole company in one go, or nothing. The company starts in Testing; only the Super Admin makes it Live, from its page.
 */
export function ClientNewPage() {
  const t = useT();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ code: '', nameEn: '', nameKm: '', slug: '', timezone: 'Asia/Phnom_Penh', contactName: '', contactPhone: '', contactEmail: '', notes: '', packageCode: '', templateCode: '', username: '', displayName: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v, ...(k === 'nameEn' && !slugTouched ? { slug: slugOf(v) } : {}) }));
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => get<PackageSummaryView[]>('/platform/packages') });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => get<TemplateSummaryView[]>('/platform/templates') });
  const offeredPackages = (packages.data ?? []).filter((p) => p.status === 'active' && p.publishedVersion);
  const offeredTemplates = (templates.data ?? []).filter((x) => x.status === 'active' && x.publishedVersion);
  useEffect(() => {
    if (!f.packageCode && offeredPackages[0]) setF((x) => ({ ...x, packageCode: offeredPackages[0]!.code }));
  }, [offeredPackages, f.packageCode]);
  const preview = useQuery({
    queryKey: ['client-preview', f.packageCode, f.templateCode],
    queryFn: () => post<ClientPreviewView>('/platform/clients/preview', { packageCode: f.packageCode, templateCode: f.templateCode || null }),
    enabled: !!f.packageCode,
  });
  const create = useMutation({
    mutationFn: () =>
      post<ClientCreatedView>('/platform/clients', {
        code: f.code,
        slug: f.slug,
        nameEn: f.nameEn,
        nameKm: f.nameKm,
        timezone: f.timezone,
        ...(f.contactName.trim() ? { contactName: f.contactName } : {}),
        ...(f.contactPhone.trim() ? { contactPhone: f.contactPhone } : {}),
        ...(f.contactEmail.trim() ? { contactEmail: f.contactEmail } : {}),
        ...(f.notes.trim() ? { notes: f.notes } : {}),
        packageCode: f.packageCode,
        templateCode: f.templateCode || null,
        admin: { username: f.username, displayName: f.displayName },
      }),
  });

  if (create.data) return <Created result={create.data} onOpen={() => nav(`/clients/${create.data.client.id}`)} />;

  const canNext = [
    /^[A-Za-z][A-Za-z0-9]{1,9}$/.test(f.code) && f.nameEn.trim() && f.nameKm.trim() && /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/.test(f.slug),
    !!preview.data && preview.data.problems.length === 0,
    /^[a-z][a-z0-9._-]{2,39}$/i.test(f.username) && f.displayName.trim(),
    true,
  ][step];
  const pkg = offeredPackages.find((p) => p.code === f.packageCode);
  const tpl = offeredTemplates.find((x) => x.code === f.templateCode);

  return (
    <>
      <Link to="/clients" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('All clients')}
      </Link>
      <PageHeader title={t('New client')} subtitle={t('A new company starts in Testing. You make it Live from its page when it is ready.')} />
      <ol className="mb-4 flex flex-wrap gap-2 text-xs" aria-label={t('Steps')}>
        {STEPS.map((s, i) => (
          <li key={s} className={cn('flex items-center gap-1.5 rounded-full px-3 py-1', i === step ? 'bg-brand-600 text-white' : i < step ? 'bg-emerald-50 text-emerald-700' : 'bg-ink-100 text-ink-500')}>
            <span className="font-semibold">{i + 1}</span> {t(s)}
          </li>
        ))}
      </ol>

      <Card>
        {step === 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('Code')}>
              <Input value={f.code} onChange={(e) => set('code')(e.target.value.toUpperCase())} maxLength={10} placeholder="GOLD" />
            </Field>
            <Field label={t('Time zone')}>
              <Select value={f.timezone} onChange={(e) => set('timezone')(e.target.value)} className="w-full">
                {CLIENT_TIMEZONES.map((z) => (
                  <option key={z}>{z}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('Name (English)')}>
              <Input value={f.nameEn} onChange={(e) => set('nameEn')(e.target.value)} maxLength={80} />
            </Field>
            <Field label={t('Name (Khmer)')}>
              <Input value={f.nameKm} onChange={(e) => set('nameKm')(e.target.value)} maxLength={80} />
            </Field>
            <Field label={t('Address name')}>
              <Input
                value={f.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  set('slug')(e.target.value.toLowerCase());
                }}
                maxLength={40}
                placeholder="gold-lottery"
              />
            </Field>
            <Field label={t('Contact name')}>
              <Input value={f.contactName} onChange={(e) => set('contactName')(e.target.value)} maxLength={80} />
            </Field>
            <Field label={t('Contact phone')}>
              <Input value={f.contactPhone} onChange={(e) => set('contactPhone')(e.target.value)} maxLength={30} />
            </Field>
            <Field label={t('Contact email')}>
              <Input value={f.contactEmail} onChange={(e) => set('contactEmail')(e.target.value)} maxLength={120} type="email" />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t('Notes')}>
                <Input value={f.notes} onChange={(e) => set('notes')(e.target.value)} maxLength={1000} />
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('Package')}>
                <Select value={f.packageCode} onChange={(e) => set('packageCode')(e.target.value)} className="w-full" aria-label={t('Package')}>
                  {offeredPackages.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.nameEn} ({p.code}) v{p.publishedVersion}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('Starter template')}>
                <Select value={f.templateCode} onChange={(e) => set('templateCode')(e.target.value)} className="w-full" aria-label={t('Starter template')}>
                  <option value="">{t('No template — start with nothing')}</option>
                  {offeredTemplates.map((x) => (
                    <option key={x.code} value={x.code}>
                      {x.nameEn} ({x.code}) v{x.publishedVersion}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {preview.isLoading ? (
              <Loading />
            ) : preview.error ? (
              <ErrorBox text={errorText(preview.error)} />
            ) : preview.data ? (
              <div>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Products the company gets')}</h3>
                {!preview.data.products.length ? (
                  <p className="text-sm text-ink-500">{t('None — install products from the company’s page.')}</p>
                ) : (
                  <ul className="space-y-1 text-sm" data-testid="preview-products">
                    {preview.data.products.map((p) => (
                      <li key={p.code} className="flex items-center gap-2">
                        {p.ok ? <Check size={14} className="text-emerald-600" /> : <X size={14} className="text-red-600" />}
                        <span className="font-mono text-xs">{p.code}</span> {p.nameEn}
                        {p.ownName && <Badge tone="blue">{t('Own name')}</Badge>}
                        {p.problem && <span className="text-xs text-red-700">{p.problem}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {preview.data.levels.length > 0 && (
                  <p className="mt-2 text-xs text-ink-600">
                    {t('Levels')}: {preview.data.levels.map((l) => l.nameEn).join(' → ')}
                  </p>
                )}
                {preview.data.problems.length > 0 && <ErrorBox text={`${t('This package and template do not fit together')}: ${preview.data.problems.join('; ')}`} />}
              </div>
            ) : null}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-ink-600">{t('The company’s first admin. A first password is made and shown once after the company is created; the admin changes it at first sign-in.')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('Username')}>
                <Input value={f.username} onChange={(e) => set('username')(e.target.value.toLowerCase())} maxLength={40} placeholder="gold.admin" autoComplete="off" />
              </Field>
              <Field label={t('Display name')}>
                <Input value={f.displayName} onChange={(e) => set('displayName')(e.target.value)} maxLength={80} />
              </Field>
            </div>
          </div>
        )}

        {step === 3 && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm" data-testid="review">
            <dt className="text-ink-500">{t('Company')}</dt>
            <dd>
              <span className="font-medium">{f.nameEn}</span> · {f.nameKm} <span className="font-mono text-xs text-ink-500">{f.code}</span>
            </dd>
            <dt className="text-ink-500">{t('Address')}</dt>
            <dd className="font-mono text-xs">
              {f.slug}.{preview.data?.rootDomain} <span className="font-sans text-ink-500">({t('served from Phase 10')})</span>
            </dd>
            <dt className="text-ink-500">{t('Package')}</dt>
            <dd>{pkg ? `${pkg.nameEn} v${pkg.publishedVersion}` : '—'}</dd>
            <dt className="text-ink-500">{t('Starter template')}</dt>
            <dd>{tpl ? `${tpl.nameEn} v${tpl.publishedVersion}` : t('None')}</dd>
            <dt className="text-ink-500">{t('Products')}</dt>
            <dd className="font-mono text-xs">{preview.data?.products.map((p) => p.code).join(', ') || '—'}</dd>
            <dt className="text-ink-500">{t('Admin')}</dt>
            <dd>
              {f.displayName} <span className="font-mono text-xs text-ink-500">{f.username}</span>
            </dd>
            <dt className="text-ink-500">{t('State')}</dt>
            <dd>
              <Badge tone="blue">{t('Testing')}</Badge>
            </dd>
          </dl>
        )}

        {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
        <div className="mt-4 flex justify-between border-t border-ink-100 pt-3">
          <Button variant="secondary" onClick={() => setStep((s) => s - 1)} disabled={step === 0}>
            {t('Back')}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
              {t('Next')}
            </Button>
          ) : (
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              {t('Create the company')}
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}

/** the company is made: its admin's first password, shown this once */
function Created({ result, onOpen }: { result: ClientCreatedView; onOpen: () => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(`${result.admin.username} / ${result.admin.initialPassword}`).then(() => setCopied(true));
  };
  return (
    <>
      <PageHeader title={`${t('Created')}: ${result.client.nameEn}`} subtitle={t('The company is in Testing.')} />
      <Card title={t('The admin’s first password — shown only now')}>
        <div className="space-y-3 text-sm">
          <p className="text-ink-600">{t('Give it to the company’s admin privately. It is not stored readable anywhere; if it is lost, make a new one from the company’s page.')}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-ink-500">{t('Username')}</dt>
            <dd className="font-mono">{result.admin.username}</dd>
            <dt className="text-ink-500">{t('First password')}</dt>
            <dd className="font-mono text-base font-semibold tracking-wider" data-testid="initial-password">
              {result.admin.initialPassword}
            </dd>
          </dl>
          <p className="text-xs text-ink-500">
            {t('Products installed')}: <span className="font-mono">{result.installed.join(', ') || '—'}</span>
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" icon={<Copy size={14} />} onClick={copy}>
              {copied ? t('Copied') : t('Copy')}
            </Button>
            <Button onClick={onOpen}>{t('Open the company')}</Button>
          </div>
        </div>
      </Card>
    </>
  );
}
